import { memo, useContext } from 'react';
import { MESSAGES } from 'src/modules/common/constants';
import { Breadcrumbs } from 'src/modules/common/types/Breadcrumbs';
import { RuntimeContext } from 'src/runtimeContext';
import { BREADCRUMB_STATUS } from 'src/common/types/BreadcrumbStatus';
import { ValvesBreadcrumbValue } from 'src/modules/common/components/ValvesBreadcrumbValue/ValvesBreadcrumbValue';
import { ConfigContext } from 'src/configContext';
import { ValvesTooltipValue } from 'src/modules/common/components/ValvesTooltipValue/ValvesTooltipValue';
import { UserInput } from 'src/modules/user-input/types';
import { PATHS } from 'src/types/Paths';
import { ValveFilterTooltip } from 'src/modules/common/components/ValveFilterTooltip/ValveFilterTooltip';
import { LocationState } from 'src/modules/LocationState';
import { BreadcrumbContainer } from '../../../../common/Breadcrumb/BreadcrumbContainer';

const VALVE_BREADCRUMB_PAGES = {
    [PATHS.Home]: 0,
    [PATHS.UserInput]: 1,
    [PATHS.Valves]: 2,
    [PATHS.Actuators]: 3,
    [PATHS.Accessories]: 4,
    [PATHS.Checkout]: 5,
};

const getPageStatus = (
    comparing: keyof typeof VALVE_BREADCRUMB_PAGES,
    comparedTo: keyof typeof VALVE_BREADCRUMB_PAGES,
) => {
    if (VALVE_BREADCRUMB_PAGES[comparing] === VALVE_BREADCRUMB_PAGES[comparedTo]) {
        return BREADCRUMB_STATUS.PROGRESS;
    }

    if (VALVE_BREADCRUMB_PAGES[comparing] < VALVE_BREADCRUMB_PAGES[comparedTo]) {
        return BREADCRUMB_STATUS.EMPTY;
    }

    return BREADCRUMB_STATUS.DONE;
};

type Props = {
    userInputState: UserInput;
    homeValue: string;
    currentPage: keyof typeof VALVE_BREADCRUMB_PAGES;
    isHomePageMandatoryMissing?: boolean;
    isUserInputPageEmptyResult?: boolean;
    locationState: LocationState;
    searchParams: URLSearchParams;
    resetHandler?: () => void;
}

export const ValveBreadcrumbs = memo(({
    userInputState,
    homeValue,
    currentPage,
    isHomePageMandatoryMissing,
    isUserInputPageEmptyResult,
    locationState,
    searchParams,
    resetHandler,
}: Props) => {
    const { userSettings } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);

    const page = locationState.valveLabel === locationState.actuatorLabel
        ? locationState.valvesPage
        : locationState.actuatorsPage;

    const isUserInputPageDisabled = VALVE_BREADCRUMB_PAGES[currentPage] < VALVE_BREADCRUMB_PAGES[PATHS.UserInput] &&
            isHomePageMandatoryMissing;
    const isValvesPageDisabled = isUserInputPageDisabled ||
        isUserInputPageEmptyResult;
    const isActuatorsPageDisabled = isUserInputPageDisabled || !locationState.valveLabel;
    const isAccessoriesPageDisabled = isActuatorsPageDisabled || !locationState.actuatorLabel;

    const breadcrumbProps: Breadcrumbs = [
        {
            title: MESSAGES.selectValveType,
            value: homeValue,
            tooltipValue: <ValveFilterTooltip value={homeValue}/>,
            linkState: locationState,
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Home),
            onActionClick: resetHandler,
            to: {
                pathname: PATHS.Home,
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.RequestedFlow,
            value: <ValvesBreadcrumbValue
                is6WayRequired={locationState.is6WayRequired}
                region={userSettings.currentRegion}
                state={userInputState}
            />,
            tooltipValue: <ValvesTooltipValue
                is6WayRequired={locationState.is6WayRequired}
                region={userSettings.currentRegion}
                state={userInputState}
            />,
            onActionClick: resetHandler,
            linkState: locationState,
            breadcrumbStatus: getPageStatus(currentPage, PATHS.UserInput),
            disabled: isUserInputPageDisabled,
            to: {
                pathname: PATHS.UserInput,
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.SelectedValve,
            disabled: isValvesPageDisabled,
            value: locationState.valveLabel || '-',
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Valves),
            linkState: locationState,
            onActionClick: resetHandler,
            to: {
                pathname: `${PATHS.Valves}/${locationState.valvesPage}`,
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.SelectedActuator,
            disabled: isActuatorsPageDisabled,
            value: locationState.actuatorLabel || '-',
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Actuators),
            linkState: locationState,
            onActionClick: resetHandler,
            to: {
                pathname: [
                    ...(locationState.valveLabel === locationState.actuatorLabel ? [
                        PATHS.Valves,
                        locationState.valvesPage,
                    ] : [
                        PATHS.Actuators,
                        encodeURIComponent(locationState.valveLabel),
                        locationState.actuatorsPage,
                    ]),
                ].join('/'),
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.SelectedAccessories,
            disabled: isAccessoriesPageDisabled,
            value: locationState.selectedAccessories.length > 0
                ? locationState.selectedAccessories.map(({ label }) => label).join(', ') : '-',
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Accessories),
            linkState: locationState,
            onActionClick: resetHandler,
            to: {
                pathname: [
                    PATHS.Accessories,
                    locationState.valveLabel,
                    locationState.actuatorLabel,
                    page,
                ].join('/'),
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.checkoutValvesBreadcrumbTitle,
            disabled: isAccessoriesPageDisabled,
            value: '',
            linkState: locationState,
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Checkout),
            onActionClick: resetHandler,
            to: {
                pathname: [
                    PATHS.Checkout,
                    locationState.valveLabel,
                    locationState.actuatorLabel,
                    page,
                ].join('/'),
                search: searchParams.toString(),
            },
        },
    ];

    return (
        <BreadcrumbContainer
            isMobile={isMobile}
            breadcrumbs={breadcrumbProps}
            containerWrapperClass="vss-valves-breadcrumbs"
        />
    );
});
